import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Sidebar, SidebarBody, SidebarLink, useSidebar } from '@unwritten/ui-patterns';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform, MotionValue } from 'framer-motion';
import { cn } from '@unwritten/ui-primitives';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import { isDetailRoute, parseDetailRoute } from './utils/routeDetection';
import { getDetailTabs } from './config/detailTabs';
import {
  Bot,
  Users,
  Gamepad2,
  Globe,
  UserCircle,
  Package,
  FileText,
  Palette,
  Music as MusicIcon,
  Settings as SettingsIcon,
  Sliders,
  Moon,
  Sun,
  Network,
  Lock,
  Unlock,
  LayoutDashboard,
  Dock,
  Plus,
  List,
  Sparkles,
  BarChart3
} from 'lucide-react';

import { AIMain } from './pages/ai/AIMain';
import { AIDetail } from './pages/ai/AIDetail';
import { ModelsMain } from './pages/ai/ModelsMain';
import { ConfigsMain as AIConfigsMain } from './pages/ai/ConfigsMain';
import { RunsMain } from './pages/ai/RunsMain';
import { AnalyticsMain } from './pages/ai/AnalyticsMain';
import { PlayersMain } from './pages/players/PlayersMain';
import { PlayerDetail } from './pages/players/PlayerDetail';
import { PlayerWizard } from './pages/players/PlayerWizard';
import { GamesMain } from './pages/games/GamesMain';
import { GameDetail } from './pages/games/GameDetail';
import { WorldsMain } from './pages/worlds/WorldsMain';
import { WorldDetail } from './pages/worlds/WorldDetail';
import { WorldWizard } from './pages/worlds/WorldWizard';
import { CharactersMain } from './pages/characters/CharactersMain';
import { CharacterDetail } from './pages/characters/CharacterDetail';
import { CharacterWizard } from './pages/characters/CharacterWizard';
import { PacksMain } from './pages/packs/PacksMain';
import { PackDetail } from './pages/packs/PackDetail';
import { PackWizard } from './pages/packs/PackWizard';
import { TemplatesMain } from './pages/templates/TemplatesMain';
import { TemplateDetail } from './pages/templates/TemplateDetail';
import { TemplateWizard } from './pages/templates/TemplateWizard';
import { ArtMain } from './pages/art/ArtMain';
import { ArtDetail } from './pages/art/ArtDetail';
import { MusicMain } from './pages/music/MusicMain';
import { MusicDetail } from './pages/music/MusicDetail';
import { ConfigsMain } from './pages/configs/ConfigsMain';
import { ConfigDetail } from './pages/configs/ConfigDetail';
import { GraphExplorer } from './pages/graph/GraphExplorer';
import { SettingsMain } from './pages/settings/SettingsMain';
import { SettingDetail } from './pages/settings/SettingDetail';
import NotFound from './pages/NotFound';

// Section sub-routes configuration
const sectionRoutes: Record<string, { label: string; path: string; icon: React.ReactNode }[]> = {
  '/ai': [
    { label: 'All Agents', path: '/ai', icon: <List className="h-4 w-4" /> },
    { label: 'Models', path: '/ai/models', icon: <Sparkles className="h-4 w-4" /> },
    { label: 'Configs', path: '/ai/configs', icon: <Sliders className="h-4 w-4" /> },
    { label: 'Runs', path: '/ai/runs', icon: <LayoutDashboard className="h-4 w-4" /> },
    { label: 'Analytics', path: '/ai/analytics', icon: <BarChart3 className="h-4 w-4" /> },
  ],
  '/players': [
    { label: 'All Players', path: '/players', icon: <List className="h-4 w-4" /> },
    { label: 'New Player', path: '/players/new', icon: <Plus className="h-4 w-4" /> },
  ],
  '/games': [
    { label: 'All Games', path: '/games', icon: <List className="h-4 w-4" /> },
  ],
  '/worlds': [
    { label: 'All Worlds', path: '/worlds', icon: <List className="h-4 w-4" /> },
    { label: 'New World', path: '/worlds/new', icon: <Plus className="h-4 w-4" /> },
  ],
  '/characters': [
    { label: 'All Characters', path: '/characters', icon: <List className="h-4 w-4" /> },
    { label: 'New Character', path: '/characters/new', icon: <Plus className="h-4 w-4" /> },
  ],
  '/packs': [
    { label: 'All Packs', path: '/packs', icon: <List className="h-4 w-4" /> },
    { label: 'New Pack', path: '/packs/new', icon: <Plus className="h-4 w-4" /> },
  ],
  '/templates': [
    { label: 'All Templates', path: '/templates', icon: <List className="h-4 w-4" /> },
    { label: 'New Template', path: '/templates/new', icon: <Plus className="h-4 w-4" /> },
  ],
  '/art': [
    { label: 'All Art', path: '/art', icon: <List className="h-4 w-4" /> },
  ],
  '/music': [
    { label: 'All Music', path: '/music', icon: <List className="h-4 w-4" /> },
  ],
  '/configs': [
    { label: 'All Primitives', path: '/configs', icon: <List className="h-4 w-4" /> },
  ],
  '/graph': [
    { label: 'Graph Explorer', path: '/graph', icon: <Sparkles className="h-4 w-4" /> },
  ],
  '/settings': [
    { label: 'All Settings', path: '/settings', icon: <List className="h-4 w-4" /> },
  ],
};

// Shared links configuration
const navigationLinks = [
    {
      label: "AI",
      href: "/ai",
      icon: <Bot className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Players",
      href: "/players",
      icon: <Users className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Games",
      href: "/games",
      icon: <Gamepad2 className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Worlds",
      href: "/worlds",
      icon: <Globe className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Characters",
      href: "/characters",
      icon: <UserCircle className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Packs",
      href: "/packs",
      icon: <Package className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Templates",
      href: "/templates",
      icon: <FileText className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Art",
      href: "/art",
      icon: <Palette className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Music",
      href: "/music",
      icon: <MusicIcon className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Primitives",
      href: "/configs",
      icon: <SettingsIcon className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Graph",
      href: "/graph",
      icon: <Network className="h-5 w-5 shrink-0" />,
    },
    {
      label: "Settings",
      href: "/settings",
      icon: <Sliders className="h-5 w-5 shrink-0" />,
  },
];

const SidebarContent = () => {
  const { open, locked, setLocked, mode, setMode } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  return (
    <SidebarBody className="justify-between gap-10">
      <div className="flex flex-1 flex-col overflow-x-hidden overflow-y-auto">
        {open ? <Logo /> : <LogoIcon />}
        <div className="mt-8 flex flex-col gap-2">
          {navigationLinks.map((link, idx) => (
            <SidebarLink key={idx} link={link} onClick={() => navigate(link.href)} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <button
          onClick={() => setMode(mode === "sidebar" ? "floating" : "sidebar")}
          className="flex items-center justify-start gap-2 py-2 px-2 rounded-md text-uw-text-secondary hover:bg-uw-surface-elevated hover:text-uw-text-primary hover:shadow-sm transition-all duration-200 w-full"
        >
          <div className="shrink-0 flex items-center justify-center w-5 h-5">
            {mode === "sidebar" ? (
              <Dock className="h-5 w-5" />
            ) : (
              <LayoutDashboard className="h-5 w-5" />
            )}
          </div>
          <motion.span
            animate={{
              display: open ? "inline-block" : "none",
              opacity: open ? 1 : 0,
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut"
            }}
            className="text-sm whitespace-nowrap"
          >
            {mode === "sidebar" ? 'Floating Mode' : 'Sidebar Mode'}
          </motion.span>
        </button>
        <button
          onClick={() => setLocked(!locked)}
          className="flex items-center justify-start gap-2 py-2 px-2 rounded-md text-uw-text-secondary hover:bg-uw-surface-elevated hover:text-uw-text-primary hover:shadow-sm transition-all duration-200 w-full"
        >
          <div className="shrink-0 flex items-center justify-center w-5 h-5">
            {locked ? (
              <Lock className="h-5 w-5" />
            ) : (
              <Unlock className="h-5 w-5" />
            )}
          </div>
          <motion.span
            animate={{
              display: open ? "inline-block" : "none",
              opacity: open ? 1 : 0,
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut"
            }}
            className="text-sm whitespace-nowrap"
          >
            {locked ? 'Unlock Sidebar' : 'Lock Sidebar'}
          </motion.span>
        </button>
        <button
          onClick={toggleTheme}
          className="flex items-center justify-start gap-2 py-2 px-2 rounded-md text-uw-text-secondary hover:bg-uw-surface-elevated hover:text-uw-text-primary hover:shadow-sm transition-all duration-200 w-full"
        >
          <div className="shrink-0 flex items-center justify-center w-5 h-5">
            {theme === 'light' ? (
              <Moon className="h-5 w-5" />
            ) : (
              <Sun className="h-5 w-5" />
            )}
          </div>
          <motion.span
            animate={{
              display: open ? "inline-block" : "none",
              opacity: open ? 1 : 0,
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut"
            }}
            className="text-sm whitespace-nowrap"
          >
            {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
          </motion.span>
        </button>
        {user && (
          <button
            onClick={handleSignOut}
            className="flex items-center justify-start gap-2 py-2 px-2 rounded-md text-uw-text-secondary hover:bg-uw-surface-elevated hover:text-uw-text-primary hover:shadow-sm transition-all duration-200 w-full"
          >
            <div className="shrink-0 flex items-center justify-center w-5 h-5">
              <div className="h-5 w-5 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-semibold">
                  {user.email?.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
            <motion.span
              animate={{
                display: open ? "inline-block" : "none",
                opacity: open ? 1 : 0,
              }}
              transition={{
                duration: 0.2,
                ease: "easeInOut"
              }}
              className="text-sm whitespace-nowrap truncate"
            >
              {user.email}
            </motion.span>
          </button>
        )}
      </div>
    </SidebarBody>
  );
};

const FloatingNav = () => {
  const { setMode } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  // Detect if we're on a detail page or list page
  const detailRoute = isDetailRoute(location.pathname) ? parseDetailRoute(location.pathname) : null;
  
  // Get active section and index
  const getActiveSection = () => {
    const link = navigationLinks.find(link => location.pathname.startsWith(link.href));
    return link ? link.href : null;
  };

  const getActiveIndex = () => {
    const index = navigationLinks.findIndex(link => location.pathname.startsWith(link.href));
    return index >= 0 ? index : undefined;
  };

  const activeSection = getActiveSection();

  // Determine what to show in nested toolbar
  let nestedToolbarItems: { label: string; path: string; icon: React.ReactNode }[] = [];
  
  if (detailRoute) {
    // On detail page - show detail tabs
    const detailTabs = getDetailTabs(detailRoute.section);
    nestedToolbarItems = detailTabs.map(tab => ({
      label: tab.label,
      path: `${detailRoute.baseSection}/${detailRoute.id}#${tab.id}`,
      icon: tab.icon,
    }));
  } else if (activeSection) {
    // On list page - show section sub-routes
    nestedToolbarItems = sectionRoutes[activeSection] || [];
  }

  // Transform navigation links for FloatingDock
  const floatingItems = [
    ...navigationLinks.map(link => ({
      title: link.label,
      icon: link.icon,
      onClick: () => navigate(link.href),
      href: link.href,
    })),
    // Add user icon as last item
    {
      title: user?.email || 'User',
      icon: (
        <div className="h-5 w-5 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center shrink-0">
          <span className="text-white text-xs font-semibold">
            {user?.email?.charAt(0).toUpperCase()}
          </span>
        </div>
      ),
      onClick: () => setShowUserMenu(!showUserMenu),
    },
  ];

  // Handle nested item clicks
  const handleNestedClick = (path: string) => {
    if (detailRoute && path.includes('#')) {
      // It's a tab change on detail page
      const hashIndex = path.indexOf('#');
      const hash = path.substring(hashIndex + 1);
      if (hash) {
        setActiveDetailTab(hash);
        window.location.hash = hash;
        // Scroll to top when changing tabs
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // It's a regular navigation
      navigate(path);
    }
  };

  // Sync active detail tab with URL hash
  useEffect(() => {
    if (detailRoute && window.location.hash) {
      const hash = window.location.hash.substring(1);
      setActiveDetailTab(hash);
    } else if (detailRoute) {
      setActiveDetailTab('overview');
    }
  }, [location.pathname, detailRoute]);

  return (
    <>
      <EnhancedFloatingDock 
        items={floatingItems} 
        activeIndex={getActiveIndex()}
        accentColor="rgb(147, 51, 234)"
        nestedItems={nestedToolbarItems}
        onNestedClick={handleNestedClick}
        currentPath={detailRoute ? `${detailRoute.baseSection}/${detailRoute.id}#${activeDetailTab}` : location.pathname}
      />
      <AnimatePresence>
        {showUserMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setShowUserMenu(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed bottom-24 right-8 z-60 bg-uw-surface-elevated rounded-lg shadow-lg border border-uw-border-default overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-uw-border-default min-w-[200px]">
                <p className="text-sm font-medium text-uw-text-primary">
                  {user?.email}
                </p>
              </div>
              <div className="flex flex-col gap-1 p-2">
                <button
                  onClick={() => {
                    toggleTheme();
                    setShowUserMenu(false);
                  }}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-uw-text-secondary hover:bg-uw-surface-overlay hover:text-uw-text-primary transition-colors rounded-md"
                >
                  {theme === 'light' ? (
                    <Moon className="h-4 w-4" />
                  ) : (
                    <Sun className="h-4 w-4" />
                  )}
                  {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                </button>
                <button
                  onClick={() => {
                    setMode("sidebar");
                    setShowUserMenu(false);
                  }}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-uw-text-secondary hover:bg-uw-surface-overlay hover:text-uw-text-primary transition-colors rounded-md"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Switch to Sidebar
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-md"
                >
                  <UserCircle className="h-4 w-4" />
                  Sign Out
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Enhanced floating dock with nested toolbar support
const EnhancedFloatingDock = ({
  items,
  activeIndex,
  accentColor,
  nestedItems,
  onNestedClick,
  currentPath,
}: {
  items: { title: string; icon: React.ReactNode; onClick: () => void; href?: string }[];
  activeIndex?: number;
  accentColor?: string;
  nestedItems?: { label: string; path: string; icon: React.ReactNode }[];
  onNestedClick?: (path: string) => void;
  currentPath?: string;
}) => {
  return (
    <>
      {/* Nested toolbar for active section */}
      <AnimatePresence mode="wait">
        {nestedItems && nestedItems.length > 0 && (
          <motion.div
            key="nested-toolbar"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ 
              type: "spring", 
              stiffness: 400, 
              damping: 30,
              mass: 0.8
            }}
            className="fixed bottom-28 left-1/2 -translate-x-1/2 z-55 hidden md:flex h-14 items-center gap-3 rounded-2xl bg-gray-50 px-3 py-2 dark:bg-neutral-900 shadow-2xl border border-gray-200 dark:border-neutral-800"
            style={{ pointerEvents: 'auto' }}
          >
            {nestedItems.map((item, idx) => {
              // Improved active state detection
              const isActive = item.path.includes('#') 
                ? currentPath === item.path // Exact match for tab hashes
                : currentPath === item.path || currentPath?.startsWith(item.path + '/'); // Path match for regular routes
              
              return (
                <motion.button
                  key={item.path}
                  onClick={() => onNestedClick?.(item.path)}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ 
                    delay: idx * 0.03,
                    type: "spring", 
                    stiffness: 400, 
                    damping: 25,
                    mass: 0.5
                  }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    "relative flex items-center justify-center h-10 px-4 rounded-full transition-all duration-200",
                    isActive
                      ? "bg-purple-600 text-white shadow-lg shadow-purple-600/30"
                      : "bg-gray-200 dark:bg-neutral-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-neutral-700"
                  )}
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    {item.icon}
                    {item.label}
                  </span>
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main floating dock */}
      <EnhancedFloatingDockDesktop
        items={items}
        activeIndex={activeIndex}
        accentColor={accentColor}
      />
    </>
  );
};

const EnhancedFloatingDockDesktop = ({
  items,
  activeIndex,
  accentColor,
}: {
  items: { title: string; icon: React.ReactNode; onClick: () => void; href?: string }[];
  activeIndex?: number;
  accentColor?: string;
}) => {
  const mouseX = useMotionValue(Infinity);

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ 
        type: "spring", 
        stiffness: 400, 
        damping: 30,
        mass: 0.8,
        delay: 0.1
      }}
      onMouseMove={(e) => mouseX.set(e.pageX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 hidden h-16 items-end gap-4 rounded-2xl bg-gray-50 px-4 pb-3 md:flex dark:bg-neutral-900 shadow-2xl border border-gray-200/50 dark:border-neutral-800"
    >
      {items.map((item, idx) => (
        <EnhancedIconContainer
          key={item.title}
          mouseX={mouseX}
          {...item}
          isActive={activeIndex === idx}
          accentColor={accentColor}
        />
      ))}
    </motion.div>
  );
};

const EnhancedIconContainer = ({
  mouseX,
  title,
  icon,
  onClick,
  isActive,
  accentColor,
  href,
}: {
  mouseX: MotionValue;
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
  isActive?: boolean;
  accentColor?: string;
  href?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);

  const distance = useTransform(mouseX, (val) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
    return val - bounds.x - bounds.width / 2;
  });

  // Active items are always enlarged (like hover state)
  const baseWidth = isActive ? 90 : 50;
  const baseHeight = isActive ? 90 : 50;
  const baseIconWidth = isActive ? 45 : 28;
  const baseIconHeight = isActive ? 45 : 28;

  const widthTransform = useTransform(
    distance,
    [-150, 0, 150],
    [baseWidth, baseWidth + 25, baseWidth]
  );
  const heightTransform = useTransform(
    distance,
    [-150, 0, 150],
    [baseHeight, baseHeight + 25, baseHeight]
  );

  const widthTransformIcon = useTransform(
    distance,
    [-150, 0, 150],
    [baseIconWidth, baseIconWidth + 12, baseIconWidth]
  );
  const heightTransformIcon = useTransform(
    distance,
    [-150, 0, 150],
    [baseIconHeight, baseIconHeight + 12, baseIconHeight]
  );

  const width = useSpring(widthTransform, {
    mass: 0.5,
    stiffness: 300,
    damping: 20,
  });
  const height = useSpring(heightTransform, {
    mass: 0.5,
    stiffness: 300,
    damping: 20,
  });

  const widthIcon = useSpring(widthTransformIcon, {
    mass: 0.5,
    stiffness: 300,
    damping: 20,
  });
  const heightIcon = useSpring(heightTransformIcon, {
    mass: 0.5,
    stiffness: 300,
    damping: 20,
  });

  return (
    <button onClick={onClick} className="relative">
      {/* Visual connector to submenu for active state */}
      {isActive && (
        <motion.div
          initial={{ opacity: 0, scaleY: 0 }}
          animate={{ opacity: 1, scaleY: 1 }}
          exit={{ opacity: 0, scaleY: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute -top-12 left-1/2 -translate-x-1/2 w-0.5 h-10 bg-gradient-to-t from-purple-600 to-transparent"
          style={{ originY: 0 }}
        />
      )}
      <motion.div
        ref={ref}
        style={{
          width,
          height,
          ...(isActive && accentColor
            ? {
                backgroundColor: accentColor,
                '--tw-ring-color': accentColor,
                '--tw-ring-opacity': '0.5',
              }
            : {}),
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={cn(
          "relative flex aspect-square items-center justify-center rounded-full transition-colors",
          isActive
            ? "ring-4 ring-offset-2 ring-offset-gray-50 dark:ring-offset-neutral-900 shadow-lg shadow-purple-600/30"
            : "bg-gray-200 dark:bg-neutral-800"
        )}
      >
        <AnimatePresence>
          {hovered && !isActive && (
            <motion.div
              initial={{ opacity: 0, y: 10, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              exit={{ opacity: 0, y: 2, x: "-50%" }}
              className="absolute -top-8 left-1/2 w-fit rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs whitespace-pre text-neutral-700 dark:border-neutral-900 dark:bg-neutral-800 dark:text-white z-70"
            >
              {title}
            </motion.div>
          )}
        </AnimatePresence>
        <motion.div
          layoutId={href ? `nav-icon-${href}` : undefined}
          style={{ width: widthIcon, height: heightIcon }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className={cn(
            "flex items-center justify-center",
            isActive && "[&>svg]:text-white dark:[&>svg]:text-white"
          )}
        >
          {icon}
        </motion.div>
      </motion.div>
    </button>
  );
};

function AppContent() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Don't show sidebar on login page
  if (location.pathname === '/login') {
    return <Login />;
  }

  return (
    <Sidebar open={open} setOpen={setOpen}>
      <NavigationWrapper />
    </Sidebar>
  );
}

function NavigationWrapper() {
  const { mode } = useSidebar();
  const location = useLocation();

  // Don't show navigation on login page
  if (location.pathname === '/login') {
    return <Login />;
  }

  return (
    <div className={cn("flex w-full h-screen overflow-hidden bg-uw-surface-default")}>
      <AnimatePresence mode="wait">
        {mode === "sidebar" && (
          <motion.div
            key="sidebar"
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 400, 
              damping: 30,
              mass: 0.8
            }}
            className="h-full"
          >
            <SidebarContent />
          </motion.div>
        )}
      </AnimatePresence>
      
      <main className={cn("flex-1 overflow-auto", mode === "floating" && "pb-24")}>
        <div className="h-full w-full">
          <Routes>
            <Route path="/" element={<Navigate to="/ai" replace />} />

            <Route path="/ai" element={<ProtectedRoute><AIMain /></ProtectedRoute>} />
            <Route path="/ai/models" element={<ProtectedRoute><ModelsMain /></ProtectedRoute>} />
            <Route path="/ai/configs" element={<ProtectedRoute><AIConfigsMain /></ProtectedRoute>} />
            <Route path="/ai/runs" element={<ProtectedRoute><RunsMain /></ProtectedRoute>} />
            <Route path="/ai/analytics" element={<ProtectedRoute><AnalyticsMain /></ProtectedRoute>} />
            <Route path="/ai/:id" element={<ProtectedRoute><AIDetail /></ProtectedRoute>} />

            <Route path="/players" element={<ProtectedRoute><PlayersMain /></ProtectedRoute>} />
            <Route path="/players/new" element={<ProtectedRoute><PlayerWizard /></ProtectedRoute>} />
            <Route path="/players/:id" element={<ProtectedRoute><PlayerDetail /></ProtectedRoute>} />

            <Route path="/games" element={<ProtectedRoute><GamesMain /></ProtectedRoute>} />
            <Route path="/games/:id" element={<ProtectedRoute><GameDetail /></ProtectedRoute>} />

            <Route path="/worlds" element={<ProtectedRoute><WorldsMain /></ProtectedRoute>} />
            <Route path="/worlds/new" element={<ProtectedRoute><WorldWizard /></ProtectedRoute>} />
            <Route path="/worlds/:id" element={<ProtectedRoute><WorldDetail /></ProtectedRoute>} />

            <Route path="/characters" element={<ProtectedRoute><CharactersMain /></ProtectedRoute>} />
            <Route path="/characters/new" element={<ProtectedRoute><CharacterWizard /></ProtectedRoute>} />
            <Route path="/characters/:id" element={<ProtectedRoute><CharacterDetail /></ProtectedRoute>} />

            <Route path="/packs" element={<ProtectedRoute><PacksMain /></ProtectedRoute>} />
            <Route path="/packs/new" element={<ProtectedRoute><PackWizard /></ProtectedRoute>} />
            <Route path="/packs/:id" element={<ProtectedRoute><PackDetail /></ProtectedRoute>} />

            <Route path="/templates" element={<ProtectedRoute><TemplatesMain /></ProtectedRoute>} />
            <Route path="/templates/new" element={<ProtectedRoute><TemplateWizard /></ProtectedRoute>} />
            <Route path="/templates/:id" element={<ProtectedRoute><TemplateDetail /></ProtectedRoute>} />

            <Route path="/art" element={<ProtectedRoute><ArtMain /></ProtectedRoute>} />
            <Route path="/art/:id" element={<ProtectedRoute><ArtDetail /></ProtectedRoute>} />

            <Route path="/music" element={<ProtectedRoute><MusicMain /></ProtectedRoute>} />
            <Route path="/music/:id" element={<ProtectedRoute><MusicDetail /></ProtectedRoute>} />

            <Route path="/configs" element={<ProtectedRoute><ConfigsMain /></ProtectedRoute>} />
            <Route path="/configs/:id" element={<ProtectedRoute><ConfigDetail /></ProtectedRoute>} />

            <Route path="/graph" element={<ProtectedRoute><GraphExplorer /></ProtectedRoute>} />

            <Route path="/settings" element={<ProtectedRoute><SettingsMain /></ProtectedRoute>} />
            <Route path="/settings/:id" element={<ProtectedRoute><SettingDetail /></ProtectedRoute>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </main>

      {mode === "floating" && <FloatingNav />}
    </div>
  );
}

const Logo = () => {
  return (
    <div className="relative z-20 flex items-center space-x-2 py-1 text-sm font-normal">
      <div className="h-5 w-6 shrink-0 rounded-tl-lg rounded-tr-sm rounded-br-lg rounded-bl-sm bg-gradient-to-br from-purple-600 to-blue-600" />
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="font-bold whitespace-pre text-uw-text-primary"
      >
        Unwritten Admin
      </motion.span>
    </div>
  );
};

const LogoIcon = () => {
  return (
    <div className="relative z-20 flex items-center space-x-2 py-1 text-sm font-normal">
      <div className="h-5 w-6 shrink-0 rounded-tl-lg rounded-tr-sm rounded-br-lg rounded-bl-sm bg-gradient-to-br from-purple-600 to-blue-600" />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
